import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Save, Send } from 'lucide-react';
import { auth } from '../../config/firebase';
import { BrandCard } from './BrandCard';
import { WaitingModal } from '../common/WaitingModal';
import { PlatformsCard } from './PlatformsCard';
import { PostContentCard } from './PostContentCard';
import { PreviewCard } from './PreviewCard';
import { PublishConfirmationModal } from './PublishConfirmationModal';
import { ScheduleCard } from './ScheduleCard';
import { TikTokComplianceCard } from './TikTokComplianceCard';
import { YouTubePostSettingsCard } from './YouTubePostSettingsCard';
import { LinkedInPostSettingsCard } from './LinkedInPostSettingsCard';
import { createPost } from '../../services/postService';
import { useBrands } from '../../hooks/useBrands';
import type { Post } from '../../types/calendar';
import type { Brand } from '../../types';
import { SocialPlatform } from '../icons/SocialIcon';

interface PostFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: Partial<Post>) => void;
  initialData?: Partial<Post> | null;
}

export const PostForm: React.FC<PostFormProps> = ({
  isOpen,
  onClose,
  onSubmit,
  initialData
}) => {
  const { t } = useTranslation();
  const [content, setContent] = useState(initialData?.content || '');
  const [selectedBrand, setSelectedBrand] = useState(initialData?.brandId || '');
  const [selectedPlatforms, setSelectedPlatforms] = useState<SocialPlatform[]>(
    initialData?.platforms as SocialPlatform[] || []
  );
  const [format, setFormat] = useState<'regular' | 'reel'>(initialData?.format || 'regular');
  const [media, setMedia] = useState<File | null>(null);
  const [mediaUrl, setMediaUrl] = useState<string | null>(initialData?.media?.url || null);
  const [mediaType, setMediaType] = useState<'image' | 'video' | null>(initialData?.media?.type || null);
  const [status, setStatus] = useState(initialData?.status || 'draft');
  const [scheduledFor, setScheduledFor] = useState<string>(initialData?.scheduledFor || '');
  const [showPublishConfirm, setShowPublishConfirm] = useState<{
    show: boolean;
    postId?: string;
  }>({ show: false });
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // TikTok settings
  const [tiktokSettings, setTiktokSettings] = useState(initialData?.tiktokSettings || {
    allowComments: false,
    allowDuets: false,
    allowStitch: false,
    discloseContent: false,
    privacy: '',
    disclosureType: [],
    userAgreedToTerms: false
  });

  // YouTube settings
  const [youtubeSettings, setYoutubeSettings] = useState(initialData?.youtubeSettings || {
    title: '',
    tags: [],
    privacy: 'public'
  });
  
  // LinkedIn settings
  const [linkedinSettings, setLinkedinSettings] = useState(initialData?.linkedinSettings || {
    organizationId: '',
    visibility: 'PUBLIC'
  });

  // Fetch brands
  const { data: brandsData, isLoading: isLoadingBrands } = useBrands();
  const brands = brandsData?.brands || [];

  // Reset form when initialData changes
  React.useEffect(() => {
    setContent(initialData?.content || '');
    setSelectedBrand(initialData?.brandId || '');
    setSelectedPlatforms(initialData?.platforms as SocialPlatform[] || []);
    setFormat(initialData?.format || 'regular');
    setMediaUrl(initialData?.media?.url || null);
    setMediaType(initialData?.media?.type || null);
    setStatus(initialData?.status || 'draft');
    setScheduledFor(initialData?.scheduledFor || '');
    setTiktokSettings(initialData?.tiktokSettings || {
      allowComments: false,
      allowDuets: false,
      allowStitch: false,
      discloseContent: false,
      privacy: '',
      disclosureType: [],
      userAgreedToTerms: false
    });
    setYoutubeSettings(initialData?.youtubeSettings || {
      title: '',
      tags: [],
      privacy: 'public'
    });
    setLinkedinSettings(initialData?.linkedinSettings || {
      organizationId: '',
      visibility: 'PUBLIC'
    });
  }, [initialData]);

  if (!isOpen) return null;

  const handleSubmit = async (shouldPublish = false) => {
    try {
      setIsSaving(true);
      setError(null);
      
      if (!selectedBrand) {
        throw new Error('Please select a brand');
      }

      if (!content.trim()) {
        throw new Error('Content cannot be empty');
      }

      // Debug media values
      console.log('Media values before submission:', {
        media,
        mediaUrl,
        mediaType,
        hasMedia: Boolean(media || mediaUrl)
      });

      // Prepare the base payload
      const basePayload: any = {
        brandId: selectedBrand,
        content,
        format: mediaType || 'text',
        platforms: selectedPlatforms,
        media: mediaUrl ? {
          url: mediaUrl,
          type: mediaType || 'image',
          ...(mediaType === 'video' ? { thumbnailUrl: mediaUrl } : {})
        } : null,
        platformSettings: {
          ...(selectedPlatforms.includes('tiktok') && {
            tiktok: {
              privacy_level: tiktokSettings.privacy,
              disable_duet: !tiktokSettings.allowDuets,
              disable_comment: !tiktokSettings.allowComments,
              disable_stitch: !tiktokSettings.allowStitch,
              user_agreed_to_terms: tiktokSettings.userAgreedToTerms,
              video_cover_timestamp_ms: 1000 // Default value
            }
          }),
          ...(selectedPlatforms.includes('youtube') && {
            youtube: {
              title: youtubeSettings.title || content.substring(0, 100),
              tags: youtubeSettings.tags,
              privacyStatus: youtubeSettings.privacy,
              contentOwner: false
            }
          }),
          ...(selectedPlatforms.includes('linkedin') && {
            linkedin: {
              organizationId: linkedinSettings.organizationId,
              visibility: linkedinSettings.visibility
            }
          })
        },
        scheduledFor: scheduledFor || null,
        status: shouldPublish ? 'pending' : 'draft',
        publishNow: false // Always set to false to support two-step verification
      };

      // Log the payload for debugging
      console.log('Submitting post with brand ID:', selectedBrand);
      
      // Get auth token
      const token = await auth.currentUser?.getIdToken();
      if (!token) {
        throw new Error('No authentication token available');
      }

      // Add ID if editing
      if (initialData?.id) {
        Object.assign(basePayload, { id: initialData.id });
      }

      // Create/update post
      let result;
      if (media) {
        // If we have media, create a FormData object
        const formData = new FormData();
        formData.append('file', media);
        formData.append('postData', JSON.stringify(basePayload));
        result = await createPost(formData, true);
      } else {
        // Otherwise just send the JSON payload
        result = await createPost(basePayload, false);
      }

      if (!result.success) {
        throw new Error(result.error || 'Failed to save post');
      }

      // Show publish confirmation modal if publish was requested
      if (shouldPublish) {
        console.log('Setting up publish confirmation with media:', {
          mediaUrl: result.post.media?.url || mediaUrl,
          mediaType: result.post.media?.type || mediaType,
          hasMedia: Boolean(result.post.media || media || mediaUrl)
        });
        
        // Update media URL from the result if available
        if (result.post.media?.url && !mediaUrl) {
          setMediaUrl(result.post.media.url);
        }
        
        // Update media type from the result if available
        if (result.post.media?.type && !mediaType) {
          setMediaType(result.post.media.type as 'image' | 'video');
        }
        
        setShowPublishConfirm({ show: true, postId: result.post.id });
      } else {
        // If just saving draft, show success message
        alert(`Draft ${initialData ? 'updated' : 'saved'} successfully!`);
        onSubmit(result.post);
        onClose();
      }
    } catch (error) {
      console.error('Error saving post:', error);
      setError(error instanceof Error ? error.message : 'Failed to save post. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Modify the brand selection handler to auto-select TikTok platform
  const handleBrandSelect = (brandId: string) => {
    setSelectedBrand(brandId);
    
    // Auto-select TikTok platform if the brand has a TikTok account
    const brandObj = brands.find((b: any) => b.id === brandId);
    if (brandObj?.social_accounts) {
      const hasTikTok = brandObj.social_accounts.some(
        (account: any) => account.platform.toLowerCase() === 'tiktok'
      );
      
      if (hasTikTok && !selectedPlatforms.includes('tiktok')) {
        setSelectedPlatforms([...selectedPlatforms, 'tiktok' as SocialPlatform]);
      }
    }
  };

  // Get TikTok username and profile image for the selected brand
  const getTikTokAccountInfo = () => {
    const selectedBrandObj = brands.find((b: any) => b.id === selectedBrand);
    if (!selectedBrandObj || !selectedBrandObj.social_accounts) return { username: '', picture_url: '' };
    
    const tiktokAccount = selectedBrandObj.social_accounts.find(
      (account: any) => account.platform.toLowerCase() === 'tiktok'
    );
    
    if (!tiktokAccount) return { username: '', picture_url: '' };
    
    return {
      username: tiktokAccount.username || '',
      picture_url: tiktokAccount.profile_url || tiktokAccount.picture_url || ''
    };
  };

  // Get all connected social accounts for the selected brand
  const getConnectedSocialAccounts = () => {
    const selectedBrandObj = brands.find((b: any) => b.id === selectedBrand);
    if (!selectedBrandObj || !selectedBrandObj.social_accounts) return [];
    
    return selectedBrandObj.social_accounts
      .filter((account: any) => account.platform && account.username)
      .map((account: any) => ({
        platform: account.platform.toLowerCase() as SocialPlatform,
        username: account.username,
        picture_url: account.profile_url || account.picture_url || ''
      }));
  };

  const tiktokAccountInfo = getTikTokAccountInfo();
  const connectedAccounts = getConnectedSocialAccounts();

  // Validation for Publish button
  const canPublish = () => {
    // Basic validations
    if (!selectedBrand || !content.trim() || selectedPlatforms.length === 0) {
      return false;
    }
    
    // TikTok validation - must agree to terms if TikTok is selected
    if (selectedPlatforms.includes('tiktok')) {
      if (!tiktokSettings.userAgreedToTerms) {
        return false;
      }
      
      // TikTok validation - must select privacy level
      if (!tiktokSettings.privacy) {
        return false;
      }

      // TikTok validation - if disclosure is enabled, options must be selected
      if (tiktokSettings.discloseContent && tiktokSettings.disclosureType.length === 0) {
        return false;
      }
    }
    
    return true;
  };

  // Get publish button tooltip
  const getPublishButtonTooltip = () => {
    if (!selectedBrand) {
      return 'Select a brand to publish';
    }
    
    if (!content.trim()) {
      return 'Add content to your post';
    }
    
    if (selectedPlatforms.length === 0) {
      return 'Select at least one platform';
    }
    
    if (selectedPlatforms.includes('tiktok') && !tiktokSettings.userAgreedToTerms) {
      return "Agree to TikTok's terms to publish";
    }
    
    if (selectedPlatforms.includes('tiktok') && !tiktokSettings.privacy) {
      return "Select a privacy level for TikTok";
    }
    
    if (selectedPlatforms.includes('tiktok') && tiktokSettings.discloseContent && tiktokSettings.disclosureType.length === 0) {
      return "You need to indicate if your content promotes yourself or a brand partner to proceed";
    }
    
    return 'Publish post now';
  };

  return (
    <div className="fixed inset-0 z-50 bg-white lg:pl-16">
      {/* Sticky Header */}
      <div className="sticky top-0 z-10 bg-white border-b border-gray-200">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <button
                onClick={onClose}
                className="p-2 rounded-lg hover:text-primary-600 transition-colors"
              >
                <ArrowLeft className="w-6 h-6" />
              </button>
              <div>
                <h1 className="text-xl sm:text-2xl lg:text-3xl font-semibold text-gray-900 font-title">
                  {initialData ? 'Edit Post' : 'Create Post'}
                </h1>
                <p className="text-sm text-gray-500">
                  {initialData ? 'Update your social media post' : 'Create a new social media post'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 w-full sm:w-auto">
              <button
                onClick={() => handleSubmit(false)}
                className="flex-1 sm:flex-initial inline-flex items-center px-4 py-2 text-primary-600 bg-white border border-primary-600 rounded-lg hover:bg-primary-50 transition-colors"
                disabled={isSaving}
              >
                <Save className="w-5 h-5 mr-2" />
                Save Draft
              </button>
              <div className="relative group">
                <button
                  onClick={() => handleSubmit(true)}
                  className={`flex-1 sm:flex-initial inline-flex items-center px-4 py-2 ${
                    canPublish() 
                      ? 'bg-primary-600 hover:bg-primary-700' 
                      : 'bg-gray-300 cursor-not-allowed opacity-50'
                  } text-white rounded-lg transition-colors`}
                  disabled={isSaving || !canPublish()}
                  title={getPublishButtonTooltip()}
                >
                  <Send className={`w-5 h-5 mr-2 ${!canPublish() ? 'opacity-50' : ''}`} />
                  Publish Now
                </button>
                {selectedPlatforms.includes('tiktok') && tiktokSettings.discloseContent && tiktokSettings.disclosureType.length === 0 && (
                  <div className="absolute top-full right-0 mt-2 w-64 p-2 bg-yellow-50 border border-yellow-200 rounded-md text-yellow-800 text-xs shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-50">
                    <div className="flex items-start">
                      <div className="flex-shrink-0 w-4 h-4 text-yellow-600 mr-1 mt-0.5">
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                      </div>
                      <span>You need to indicate if your content promotes yourself or a brand partner to proceed.</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {error && (
            <div className="mt-2 p-2 bg-red-50 text-red-600 text-sm rounded-lg">
              {error}
            </div>
          )}
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="h-[calc(100vh-73px)] overflow-y-auto">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-6">
          <div className="grid grid-cols-12 gap-4 sm:gap-6">
            <div className="col-span-12 lg:col-span-8 space-y-4 sm:space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                <BrandCard
                  selectedBrand={selectedBrand}
                  onBrandSelect={handleBrandSelect}
                  isLoading={isLoadingBrands}
                  brands={brands}
                />

                <PlatformsCard
                  selectedPlatforms={selectedPlatforms}
                  onPlatformToggle={(platform) => {
                    if (selectedPlatforms.includes(platform)) {
                      setSelectedPlatforms(selectedPlatforms.filter(p => p !== platform));
                    } else {
                      setSelectedPlatforms([...selectedPlatforms, platform]);
                    }
                  }}
                  onFormatChange={setFormat}
                  format={format}
                  connectedAccounts={connectedAccounts}
                  tiktokAccount={tiktokAccountInfo}
                />
              </div>

              <PostContentCard
                content={content}
                onContentChange={setContent}
                media={media}
                onMediaChange={setMedia}
                existingMediaUrl={mediaUrl}
                existingMediaType={mediaType}
                status={status}
                format={format}
                onFormatChange={setFormat}
                brandId={selectedBrand}
              />

              {selectedPlatforms.includes('tiktok') && (
                <TikTokComplianceCard
                  {...tiktokSettings}
                  tiktokUsername={tiktokAccountInfo.username}
                  tiktokProfileImage={tiktokAccountInfo.picture_url}
                  onAllowCommentsChange={(allow) =>
                    setTiktokSettings({ ...tiktokSettings, allowComments: allow })
                  }
                  onAllowDuetsChange={(allow) =>
                    setTiktokSettings({ ...tiktokSettings, allowDuets: allow })
                  }
                  onAllowStitchChange={(allow) =>
                    setTiktokSettings({ ...tiktokSettings, allowStitch: allow })
                  }
                  onPrivacyChange={(privacy) =>
                    setTiktokSettings({ ...tiktokSettings, privacy })
                  }
                  onDiscloseContentChange={(disclose, type) =>
                    setTiktokSettings({
                      ...tiktokSettings,
                      discloseContent: disclose,
                      disclosureType: type ? type.split(',') : []
                    })
                  }
                  onUserAgreedToTermsChange={(agreed) =>
                    setTiktokSettings({
                      ...tiktokSettings,
                      userAgreedToTerms: agreed
                    })
                  }
                />
              )}

              {selectedPlatforms.includes('youtube') && (
                <YouTubePostSettingsCard
                  privacy={youtubeSettings.privacy}
                  title={youtubeSettings.title}
                  tags={youtubeSettings.tags}
                  onTitleChange={(title) =>
                    setYoutubeSettings({ ...youtubeSettings, title })
                  }
                  onPrivacyChange={(privacy) =>
                    setYoutubeSettings({ ...youtubeSettings, privacy })
                  }
                  onTagsChange={(tags) =>
                    setYoutubeSettings({ ...youtubeSettings, tags })
                  }
                />
              )}

              {selectedPlatforms.includes('linkedin') && (
                <LinkedInPostSettingsCard
                  organizationId={linkedinSettings.organizationId}
                  visibility={linkedinSettings.visibility}
                  selectedBrandId={selectedBrand}
                  onOrganizationIdChange={(organizationId) =>
                    setLinkedinSettings({ ...linkedinSettings, organizationId })
                  }
                  onVisibilityChange={(visibility) =>
                    setLinkedinSettings({ ...linkedinSettings, visibility })
                  }
                />
              )}
            </div>

            <div className="col-span-12 lg:col-span-4 space-y-4 sm:space-y-6">
              <ScheduleCard
                scheduledFor={scheduledFor}
                onScheduleChange={setScheduledFor}
              />

              <PreviewCard
                content={content}
                selectedPlatforms={selectedPlatforms}
                media={media}
                existingMediaUrl={mediaUrl}
                existingMediaType={mediaType}
                format={format}
                selectedBrand={selectedBrand}
                brands={brands}
              />
            </div>
          </div>
        </div>
      </div>

      <WaitingModal
        isOpen={isSaving}
        title="Saving Draft"
        message="Please wait while we save your post..."
        gifUrl="https://media.socialness.ai/loading/unicorn.gif"
      />

      <PublishConfirmationModal
        isOpen={showPublishConfirm.show}
        onClose={() => setShowPublishConfirm({ show: false })}
        onConfirm={async () => {
          try {
            // Handle publish confirmation
            onSubmit({ id: showPublishConfirm.postId });
            onClose();
          } catch (error) {
            console.error('Error publishing post:', error);
            // Removed the alert as it can appear as a false negative
          }
          setShowPublishConfirm({ show: false });
        }}
        postId={showPublishConfirm.postId || ''}
        content={content}
        platforms={selectedPlatforms}
        brand={brands.find(b => b.id === selectedBrand) || { id: selectedBrand, name: 'Unknown Brand', logo: '' }}
        hasMedia={Boolean(media || mediaUrl)}
        mediaType={mediaType}
        format={format}
        scheduledFor={scheduledFor}
        media={media}
        mediaUrl={mediaUrl}
        tiktokSettings={tiktokSettings}
      />
    </div>
  );
};